// src/app/pages/admin/AdminChatPage.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { 
  MessageSquare, 
  WifiOff, 
  User, 
  Users, 
  RefreshCw,
  Clock
} from "lucide-react";

import { adminApi } from "../../features/admin/adminApi.js";
import { listMessages, sendMessage, markRead } from "../../features/chat/chatApi.js";
import { useChatSocket } from "../../features/chat/useChatSocket.js";
import { useAuthSession } from "../../features/auth/useAuth.js";

import MessageBubble from "../../components/chat/MessageBubble.jsx";
import ChatComposer from "../../components/chat/ChatComposer.jsx";

function uuidLike() {
  return `c_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

export default function AdminChatPage() {
  const qc = useQueryClient();
  const bottomRef = useRef(null);
  const { user } = useAuthSession();

  const [statusFilter, setStatusFilter] = useState("UNASSIGNED"); // ALL | UNASSIGNED | ASSIGNED
  const [selectedThreadId, setSelectedThreadId] = useState(null);
  const [assignBusy, setAssignBusy] = useState(false);
  const [sending, setSending] = useState(false);

  // 1) Inbox list using adminApi
  const threadsQ = useQuery({
    queryKey: ["adminChatThreads", statusFilter],
    queryFn: () => adminApi.listChatThreads({ status: statusFilter, limit: 100, offset: 0 }),
    staleTime: 5000,
    refetchInterval: 10000,
  });

  const threads = threadsQ.data?.items || [];

  // Auto-select first thread if none selected
  useEffect(() => {
    if (!selectedThreadId && threads.length > 0) {
      setSelectedThreadId(threads[0].threadId);
    }
  }, [threads, selectedThreadId]);

  // 2) Load messages for selected thread
  const messagesQ = useQuery({
    queryKey: ["adminChatMessages", selectedThreadId],
    enabled: !!selectedThreadId,
    queryFn: () => listMessages({ threadId: selectedThreadId, limit: 80 }),
    staleTime: 2000,
  });

  const baseMessages = messagesQ.data?.items || [];

  const scrollToBottom = () => bottomRef.current?.scrollIntoView({ behavior: "smooth" });

  useEffect(() => {
    scrollToBottom();
  }, [baseMessages.length]);

  // 3) WS for selected thread
  const onWsMessage = useCallback(
    (data) => {
      if (!data || !selectedThreadId) return;

      console.log('📨 WebSocket message received:', data);

      if (data.type === "message" && data.message) {
        const msg = data.message;

        qc.setQueryData(["adminChatMessages", selectedThreadId], (old) => {
          const prev = old?.items || [];
          const exists = prev.some(
            (m) =>
              (m.id && m.id === msg.id) ||
              (m.clientMessageId && m.clientMessageId === msg.clientMessageId)
          );
          if (exists) return old;
          return { ...(old || {}), items: [...prev, msg] };
        });

        // Mark as read
        if (msg.id) {
          markRead({ threadId: selectedThreadId, lastReadMessageId: msg.id }).catch(() => {});
        }
        
        scrollToBottom();
      }
    },
    [qc, selectedThreadId]
  );

  const ws = useChatSocket({ threadId: selectedThreadId, onMessage: onWsMessage });

  // 4) Fallback polling if WS disconnected
  useEffect(() => {
    if (!selectedThreadId) return;
    if (ws.status === "connected") return;

    const t = window.setInterval(() => {
      qc.invalidateQueries({ queryKey: ["adminChatMessages", selectedThreadId] });
    }, 4000);

    return () => window.clearInterval(t);
  }, [ws.status, selectedThreadId, qc]);

  // 5) Send message
  const sendText = async (text) => {
    if (!selectedThreadId || sending) return;
    
    setSending(true);
    const clientMessageId = uuidLike();

    try {
      await sendMessage({
        threadId: selectedThreadId,
        clientMessageId,
        kind: "text",
        text,
      });

      // Optimistically add to UI
      qc.setQueryData(["adminChatMessages", selectedThreadId], (old) => {
        const prev = old?.items || [];
        const newMsg = {
          id: clientMessageId,
          clientMessageId,
          kind: "text",
          text,
          sender: "ME",
          status: "sent",
          createdAt: new Date().toISOString(),
          createdAtLabel: new Date().toLocaleTimeString(),
        };
        return { ...(old || {}), items: [...prev, newMsg] };
      });

      scrollToBottom();
    } catch (error) {
      console.error('Failed to send message:', error);
    } finally {
      setSending(false);
    }
  };

  // 6) Assign thread using adminApi
  const onAssign = async () => {
    if (!selectedThreadId) return;
    setAssignBusy(true);
    try {
      await adminApi.assignChatThread(selectedThreadId);
      await threadsQ.refetch();
    } catch (error) {
      console.error('Failed to assign thread:', error);
    } finally {
      setAssignBusy(false);
    }
  };

  const selectedThread = useMemo(
    () => threads.find((t) => t.threadId === selectedThreadId) || null,
    [threads, selectedThreadId]
  );

  return (
    <div className="h-[calc(100vh-6rem)] grid grid-cols-1 lg:grid-cols-[360px_1fr] gap-4 p-4">
      {/* Left: inbox */}
      <div className="bg-gray-900 border border-gray-800 rounded-2xl overflow-hidden flex flex-col">
        <div className="p-4 border-b border-gray-800 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-2 text-white font-bold">
            <MessageSquare size={18} />
            Chat Inbox
            {threadsQ.isFetching && (
              <RefreshCw size={14} className="animate-spin text-gray-400 ml-2" />
            )}
          </div>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="bg-gray-800 border border-gray-700 text-gray-100 text-xs rounded-lg px-2 py-1 focus:outline-none focus:border-indigo-500"
          >
            <option value="UNASSIGNED">Unassigned</option>
            <option value="ASSIGNED">Assigned</option>
            <option value="ALL">All</option>
          </select>
        </div>

        <div className="flex-1 overflow-y-auto p-3">
          {threadsQ.isLoading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500"></div>
            </div>
          ) : threads.length === 0 ? (
            <div className="text-sm text-gray-400 text-center py-8">
              No chat threads found.
            </div>
          ) : (
            <div className="space-y-2">
              {threads.map((t) => {
                const active = t.threadId === selectedThreadId;
                const isAssigned = t.moderatorId !== null;
                
                return (
                  <button
                    key={t.threadId}
                    onClick={() => setSelectedThreadId(t.threadId)}
                    className={`w-full text-left rounded-xl px-3 py-3 border transition-all ${
                      active
                        ? "bg-indigo-500/10 border-indigo-500/30 shadow-lg shadow-indigo-500/5"
                        : "bg-gray-950/30 border-gray-800 hover:bg-gray-950/60"
                    }`}
                    type="button"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <User size={14} className="text-gray-400" />
                        <span className="text-xs text-gray-400 truncate max-w-[120px]">
                          {t.traderEmail || t.traderId || 'User'}
                        </span>
                      </div>
                      <div className="text-[11px] text-gray-500">
                        {t.lastMessageAt ? new Date(t.lastMessageAt).toLocaleString() : "—"}
                      </div>
                    </div>

                    <div className="mt-1 text-xs text-gray-400 flex items-center gap-2">
                      {isAssigned ? (
                        <>
                          <Users size={12} className="text-green-400" />
                          <span className="text-green-400/70">Assigned</span>
                        </>
                      ) : (
                        <>
                          <Clock size={12} className="text-amber-400" />
                          <span className="text-amber-400/70">Unassigned</span>
                        </>
                      )}
                    </div>

                    {t.preview && (
                      <div className="mt-1 text-[11px] text-gray-500 truncate">
                        {t.preview}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="p-3 border-t border-gray-800 text-xs text-gray-500 flex-shrink-0 flex justify-between">
          <span>{threads.length} thread{threads.length !== 1 ? 's' : ''}</span>
          <button
            onClick={() => threadsQ.refetch()}
            className="text-indigo-400 hover:text-indigo-300 transition-colors"
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Right: chat */}
      <div className="bg-gray-900 border border-gray-800 rounded-2xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="p-4 border-b border-gray-800 flex items-center justify-between flex-shrink-0">
          <div>
            <div className="text-white font-bold flex items-center gap-2">
              <MessageSquare size={16} />
              {selectedThread ? (
                <span className="text-sm truncate max-w-[200px]">
                  {selectedThread.traderEmail || selectedThread.traderId || 'Thread'}
                </span>
              ) : (
                'Select a thread'
              )}
            </div>
            <div className="text-xs text-gray-400">
              {selectedThread ? `ID: ${selectedThread.threadId}` : "—"}
            </div>
          </div>

          <div className="flex items-center gap-3">
            {ws.status !== "connected" ? (
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-amber-500/30 bg-amber-500/10 text-amber-200 text-xs">
                <WifiOff size={14} />
                Reconnecting…
              </div>
            ) : (
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-emerald-500/30 bg-emerald-500/10 text-emerald-200 text-xs">
                <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
                Connected
              </div>
            )}

            <button
              onClick={onAssign}
              disabled={!selectedThreadId || assignBusy}
              className="px-3 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white text-xs disabled:opacity-60 transition-colors"
              type="button"
              title="Assign this thread to you"
            >
              {assignBusy ? "Assigning…" : "Assign to me"}
            </button>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950">
          {messagesQ.isLoading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500"></div>
            </div>
          ) : baseMessages.length === 0 ? (
            <div className="text-sm text-gray-400 text-center py-8">
              No messages yet.
              <br />
              <span className="text-xs text-gray-500">Waiting for user to start a conversation.</span>
            </div>
          ) : (
            baseMessages.map((m) => (
              <MessageBubble 
                key={m.id || m.clientMessageId} 
                msg={m} 
              />
            ))
          )}
          <div ref={bottomRef} />
        </div>

        {/* Composer (admin/mod) */}
        <ChatComposer
          disabled={!selectedThreadId || messagesQ.isLoading || sending}
          onSendText={sendText}
          onSendGifUrl={async () => {}}
          onSendFile={async () => {}}
          onSendVoiceBlob={async () => {}}
        />
      </div>
    </div>
  );
}
